import React, { useEffect, useMemo, useRef } from "react";
import colormap from "colormap";

/**
 * baseImage 위에 scoremapImages(그레이스케일 히트맵)를 컬러맵으로 착색해 합성하고,
 * 선택적으로 돋보기(lens) 확대를 제공한다.
 *
 * 설계 원칙
 * - 레이아웃 계산은 CSS(object-fit: contain)에 맡긴다. ResizeObserver를 쓰지 않는다.
 * - 중간 결과는 오프스크린 캔버스로 ref에 보관한다. toDataURL / state를 거치지 않는다.
 * - 렌즈는 drawImage의 소스 사각형으로 그린다. position: fixed라 레이아웃에 기여하지 않는다.
 */
export default function ScoremapOverlay({
  scoremapImages,
  baseImage,

  blend = "max", // "max" | "avg" | "add"
  opacity = 0.5,

  // colormap
  cmap = "rainbow",
  cmapShades = 256,
  cmapReverse = false,
  cmapAlpha = 1,

  // 캔버스 해상도 (미지정 시 첫 스코어맵의 원본 크기)
  width,
  height,

  // 확대
  enableZoom = true,
  zoom = 2,
  zoomMode = "lens", // "lens" | "hover" | "off"

  // 렌즈
  lensSize = 200,
  lensBorder = "2px solid #fff",
  lensShadow = "0 4px 16px rgba(0,0,0,0.35)",
  lensSource = "composite", // "composite" | "base" | "overlay"
  lensContentOpacity = 1,
  lensBlendMode = "normal",
  lensPixelated = false, // 고배율에서 픽셀을 또렷하게 볼지

  className,
  style,
  onError,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const lensRef = useRef(null);
  const lensCanvasRef = useRef(null);

  // 오프스크린 레이어. state가 아니라 ref에 보관한다.
  const layersRef = useRef({ base: null, overlay: null, composite: null, W: 0, H: 0 });

  const rafRef = useRef(0);
  const listeningRef = useRef(false);
  const shownRef = useRef(false);

  const lensActive = enableZoom && zoomMode === "lens";
  const hoverActive = enableZoom && zoomMode === "hover";

  // ---------- 컬러맵 LUT ----------
  // 256개 엔트리의 평평한 RGBA 배열. 픽셀 루프에서 배열을 새로 만들지 않기 위함.
  const lut = useMemo(() => {
    const shades = clampInt(cmapShades, 2, 256);
    let table;
    try {
      table = colormap({ colormap: cmap, nshades: shades, format: "rgb" });
    } catch {
      table = [
        [0, 0, 0],
        [255, 255, 255],
      ];
    }
    if (cmapReverse) table = table.slice().reverse();

    const last = table.length - 1;
    const alpha = Math.round(255 * clamp01(opacity) * clamp01(cmapAlpha));
    const out = new Uint8ClampedArray(256 * 4);
    for (let v = 0; v < 256; v++) {
      const [r, g, b] = table[Math.round((v / 255) * last)] || [0, 0, 0];
      const o = v << 2;
      out[o] = r;
      out[o + 1] = g;
      out[o + 2] = b;
      out[o + 3] = alpha;
    }
    return out;
  }, [cmap, cmapShades, cmapReverse, cmapAlpha, opacity]);

  // ---------- dep 안정화 ----------
  // 부모가 매 렌더 새 배열을 넘겨도 파이프라인이 재실행되지 않도록,
  // 내용 기반의 가벼운 키를 만든다. (data URL 전체를 join하면 비싸므로 지문만 쓴다)
  const scoremapKey = useMemo(() => fingerprint(scoremapImages), [scoremapImages]);
  const baseKey = useMemo(() => fingerprint(baseImage ? [baseImage] : []), [baseImage]);

  // ---------- 합성 파이프라인 ----------
  useEffect(() => {
    const srcs = scoremapImages ?? [];
    if (!srcs.length && !baseImage) return;

    let cancelled = false;

    (async () => {
      try {
        // 한 장이 깨져도 나머지로 그린다. Promise.all이면 한 장 때문에 전부 안 그려진다.
        const settled = await Promise.allSettled(srcs.map(loadImage));
        if (cancelled) return;
        for (const r of settled) if (r.status === "rejected") (onError ?? console.error)(r.reason);
        const maps = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);

        let W = width;
        let H = height;
        if (!W || !H) {
          // 실제 이미지 비율은 base 기준. scoremap은 모델 입력 크기(정사각형 등)로 올 수 있어 기준으로 쓰면 base가 찌그러진다
          const ref = (baseImage ? await loadImage(baseImage).catch(() => null) : null) ?? maps[0];
          if (cancelled || !ref) return;
          W = W ?? ref.naturalWidth;
          H = H ?? ref.naturalHeight;
        }
        if (!W || !H) return;

        // base 레이어
        const base = makeCanvas(W, H);
        if (baseImage) {
          const b = await loadImage(baseImage);
          if (cancelled) return;
          base.getContext("2d").drawImage(b, 0, 0, W, H);
        }

        // 스코어맵 누적
        const accum = new Float32Array(W * H);
        if (maps.length) {
          const scratch = makeCanvas(W, H);
          const sctx = scratch.getContext("2d", { willReadFrequently: true });
          for (const img of maps) {
            sctx.clearRect(0, 0, W, H);
            sctx.drawImage(img, 0, 0, W, H);
            const { data } = sctx.getImageData(0, 0, W, H);
            if (blend === "max") {
              for (let i = 0, p = 0; p < accum.length; i += 4, p++) {
                if (data[i] > accum[p]) accum[p] = data[i];
              }
            } else {
              for (let i = 0, p = 0; p < accum.length; i += 4, p++) {
                accum[p] += data[i];
              }
            }
          }

          if (blend === "avg") {
            for (let p = 0; p < accum.length; p++) accum[p] /= maps.length;
          } else if (blend === "add") {
            let maxVal = 1;
            for (let p = 0; p < accum.length; p++) {
              if (accum[p] > maxVal) maxVal = accum[p];
            }
            const scale = 255 / maxVal;
            for (let p = 0; p < accum.length; p++) accum[p] *= scale;
          }
        }

        // 착색
        const overlay = makeCanvas(W, H);
        const octx = overlay.getContext("2d");
        const imageData = octx.createImageData(W, H);
        const d = imageData.data;
        for (let p = 0, i = 0; p < accum.length; p++, i += 4) {
          const o = (accum[p] < 0 ? 0 : accum[p] > 255 ? 255 : accum[p] | 0) << 2;
          d[i] = lut[o];
          d[i + 1] = lut[o + 1];
          d[i + 2] = lut[o + 2];
          d[i + 3] = lut[o + 3];
        }
        octx.putImageData(imageData, 0, 0);

        // 합성
        const composite = makeCanvas(W, H);
        const cctx = composite.getContext("2d");
        cctx.drawImage(base, 0, 0);
        cctx.drawImage(overlay, 0, 0);

        if (cancelled) return;
        layersRef.current = { base, overlay, composite, W, H };

        // 화면 캔버스에 출력
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(composite, 0, 0);
      } catch (e) {
        if (!cancelled) (onError ?? console.error)(e);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoremapKey, baseKey, blend, width, height, lut]);

  // ---------- 렌즈 ----------
  const drawLens = (clientX, clientY) => {
    const wrap = wrapRef.current;
    const shell = lensRef.current;
    const lc = lensCanvasRef.current;
    const { W, H } = layersRef.current;
    if (!wrap || !shell || !lc || !W) return;

    const src = layersRef.current[lensSource] ?? layersRef.current.composite;
    if (!src) return;

    // object-fit: contain 기하를 그대로 재현한다.
    const rect = wrap.getBoundingClientRect();
    const scale = Math.min(rect.width / W, rect.height / H);
    const drawW = W * scale;
    const drawH = H * scale;
    const originX = rect.left + (rect.width - drawW) / 2;
    const originY = rect.top + (rect.height - drawH) / 2;

    if (clientX < originX || clientX > originX + drawW || clientY < originY || clientY > originY + drawH) {
      hideLens();
      return;
    }

    if (!shownRef.current) {
      shell.style.display = "block";
      shownRef.current = true;
    }
    shell.style.transform = `translate3d(${clientX - lensSize / 2}px, ${clientY - lensSize / 2}px, 0)`;

    // 이미지 픽셀 좌표
    const ix = (clientX - originX) / scale;
    const iy = (clientY - originY) / scale;

    // 렌즈에 담을 소스 영역 (이미지 픽셀 단위)
    const srcSize = lensSize / (scale * zoom);
    const k = lensSize / srcSize; // 소스 1px당 렌즈 px

    let sx = ix - srcSize / 2;
    let sy = iy - srcSize / 2;
    let sw = srcSize;
    let sh = srcSize;
    let dx = 0;
    let dy = 0;
    let dw = lensSize;
    let dh = lensSize;

    // 가장자리에서 소스 사각형이 이미지를 벗어나면 잘라내고 대상도 같은 비율로 줄인다.
    if (sx < 0) {
      dx = -sx * k;
      dw += sx * k;
      sw += sx;
      sx = 0;
    }
    if (sy < 0) {
      dy = -sy * k;
      dh += sy * k;
      sh += sy;
      sy = 0;
    }
    if (sx + sw > W) {
      const over = sx + sw - W;
      sw -= over;
      dw -= over * k;
    }
    if (sy + sh > H) {
      const over = sy + sh - H;
      sh -= over;
      dh -= over * k;
    }

    const ctx = lc.getContext("2d");
    ctx.setTransform(lc._dpr, 0, 0, lc._dpr, 0, 0);
    ctx.clearRect(0, 0, lensSize, lensSize);
    if (sw > 0 && sh > 0) {
      ctx.imageSmoothingEnabled = !lensPixelated;
      ctx.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh);
    }
  };

  const onPointerMove = (e) => {
    if (rafRef.current) return;
    const { clientX, clientY } = e;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      drawLens(clientX, clientY);
    });
  };

  const hideLens = () => {
    if (lensRef.current) lensRef.current.style.display = "none";
    shownRef.current = false;
  };

  // 팝업 창(portal)에서도 동작하도록 요소가 속한 window에 리스너를 건다
  const winRef = useRef(window);

  const startTracking = () => {
    if (listeningRef.current || !lensActive) return;
    const win = (winRef.current = wrapRef.current?.ownerDocument.defaultView ?? window);
    win.addEventListener("pointermove", onPointerMove, { passive: true });
    win.addEventListener("blur", stopTracking);
    // fixed 위치이므로 스크롤 중에는 좌표가 어긋난다. 그냥 숨긴다.
    win.addEventListener("scroll", stopTracking, true);
    listeningRef.current = true;
  };

  const stopTracking = () => {
    if (!listeningRef.current) return;
    winRef.current.removeEventListener("pointermove", onPointerMove);
    winRef.current.removeEventListener("blur", stopTracking);
    winRef.current.removeEventListener("scroll", stopTracking, true);
    listeningRef.current = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    hideLens();
  };

  // 렌즈 캔버스 해상도 (DPR 반영)
  useEffect(() => {
    const lc = lensCanvasRef.current;
    if (!lc) return;
    const dpr = window.devicePixelRatio || 1;
    lc.width = Math.round(lensSize * dpr);
    lc.height = Math.round(lensSize * dpr);
    lc.style.width = `${lensSize}px`;
    lc.style.height = `${lensSize}px`;
    lc._dpr = dpr;
  }, [lensSize, lensActive]);

  // 언마운트 정리
  useEffect(() => stopTracking, []);
  useEffect(() => {
    if (!lensActive) stopTracking();
  }, [lensActive]);

  // ---------- hover 확대 (렌즈를 쓰지 않을 때) ----------
  const applyHoverZoom = (e) => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * 100;
    const py = ((e.clientY - rect.top) / rect.height) * 100;
    canvas.style.transformOrigin = `${px}% ${py}%`;
    canvas.style.transform = `scale(${zoom})`;
  };

  const clearHoverZoom = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.style.transform = "none";
  };

  return (
    <div
      ref={wrapRef}
      className={className}
      onPointerEnter={lensActive ? startTracking : undefined}
      onPointerLeave={lensActive ? stopTracking : hoverActive ? clearHoverZoom : undefined}
      onPointerMove={hoverActive ? applyHoverZoom : undefined}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
          transition: hoverActive ? "transform 0.15s ease" : "none",
        }}
      />

      {lensActive && (
        <div
          ref={lensRef}
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            display: "none",
            width: lensSize,
            height: lensSize,
            border: lensBorder,
            boxShadow: lensShadow,
            borderRadius: "50%",
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 9999,
            boxSizing: "content-box",
            opacity: lensContentOpacity,
            mixBlendMode: lensBlendMode,
            willChange: "transform",
          }}
        >
          <canvas ref={lensCanvasRef} style={{ display: "block", borderRadius: "50%" }} />
        </div>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampInt(v, lo, hi) {
  const n = Math.round(Number(v) || lo);
  return n < lo ? lo : n > hi ? hi : n;
}

function makeCanvas(w, h) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // data URL이 아닐 때만 CORS를 요청한다. 불필요한 실패를 막기 위함.
    if (!/^data:/.test(src)) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`이미지 로드 실패: ${String(src).slice(0, 64)}`));
    img.src = src;
  });
}

// 긴 data URL 전체를 이어붙이지 않고 길이 + 앞부분만으로 지문을 만든다.
function fingerprint(list) {
  if (!list?.length) return "";
  return list.map((s) => `${s.length}:${s.slice(0, 32)}`).join("|");
}
